import "amazon-connect-streams";
import "amazon-connect-chatjs";
import "amazon-connect-taskjs";

import {
  CONTACT_STATE_MAP,
  CCP_STATES,
  CCP_STATES_INDEX,
  LOG_PREFIX,
  CCP_INSTANCE,
} from "../../constants/constants";

import { Call } from "../models/call";
import { subscribeToAgentEvents } from "./agent";
import { subscribeToContactEvents } from "./contact";
import { Voicemail } from "../models/voicemail";
import { getAwsRegion, getCCPUrl } from "../../config";
import Emitter from "../../emitter";
import { CCP_STATE, CONNECT_STREAMS_DIV_ID, STOP_CCP, UPDATE_MEDIA_DEVICES_LIST } from "../constants";
import {
  TELEMETRY_INFO,
  TELEMETRY_ERROR,
  INIT_CCP,
  FAIL_SESSION,
  API_UPDATE_RING_MEDIA_DEVICE,
  API_UPDATE_SPEAKER_DEVICE,
  API_UPDATE_MICROPHONE_DEVICE,
  STATE_UPDATE_MICROPHONE_DEVICE,
} from "../../constants/events";
import { addCCPProfile } from "../../api/session-api";
import { Callback } from "../../types/callback.type";
import { ConferenceState } from "../../types/call.type";
import { MediaDevice } from "../../types/webrtc.type";
const lp = LOG_PREFIX + " [handlers.session.ts]: ";

class AmazonConnectSession {
  agent?: any;
  contact?: any;
  currCCPstate: number = -1;
  call: Call | null = null;
  voicemail: Voicemail | null = null;
  callback: Callback | null = null;
  conference: ConferenceState | null = null;
  static instance: any;
  ccpUrl: string = "";
  region: string = "";
  instanceAlias: string = "";
  missedContactThreshold: number = 2;

  constructor() {
    this.initiateCcpSession = this.initiateCcpSession.bind(this);
    this.terminatCcpSession = this.terminatCcpSession.bind(this);
    this.getCall = this.getCall.bind(this);
    this.setCall = this.setCall.bind(this);
    this.getVoicemail = this.getVoicemail.bind(this);
    this.setVoicemail = this.setVoicemail.bind(this);
    this.getCallback = this.getCallback.bind(this);
    this.setCallback = this.setCallback.bind(this);
    this.getConferenceCall = this.getConferenceCall.bind(this);
    this.setConferenceCall = this.setConferenceCall.bind(this);
    this.logoutFromConnect = this.logoutFromConnect.bind(this);
    this.terminatCcpInstanceSession =
      this.terminatCcpInstanceSession.bind(this);
    this.updateCCPstateToBg = this.updateCCPstateToBg.bind(this);
    this.getMediaDevices = this.getMediaDevices.bind(this);
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new AmazonConnectSession();
    }
    return this.instance;
  }

  async initiateCcpSession(params: {
    ccpUrl: string;
    region: string;
    instanceAlias: any;
    missedContactThreshold: number;
    enable_debug_logging?: boolean;
  }) {
    this.ccpUrl = params.ccpUrl;
    this.region = params.region;
    this.instanceAlias = params.instanceAlias;
    console.log(
      lp +
      "ccp start event called, current ccp state is = " +
      CCP_STATES_INDEX[this.currCCPstate]
    );
    if (this.currCCPstate !== CCP_STATES.CONNECTED) {
      this.updateCCPstateToBg(CCP_STATES.OPEN);
      // initialize the ccp
      try {
        const containerDiv = document.getElementById(
          CONNECT_STREAMS_DIV_ID
        ) as HTMLElement;
        console.log(
          lp + "Container div for initiating ccp connection ",
          containerDiv
        );
        if (
          ![CCP_STATES.CONNECTING, CCP_STATES.CONNECTED].includes(
            this.currCCPstate
          )
        ) {
          await addCCPProfile();
          this.missedContactThreshold = params.missedContactThreshold || 2;
          this.updateCCPstateToBg(CCP_STATES.CONNECTING);

          const logLevel = params.enable_debug_logging === true
            ? connect.LogLevel.DEBUG
            : connect.LogLevel.INFO;
          connect.getLog().setLogLevel(logLevel);
          connect.getLog().setEchoLevel(logLevel);

          // Listen browser media devices changes
          navigator.mediaDevices.addEventListener('devicechange', this.getMediaDevices);
          connect.core.initCCP(containerDiv, {
            ccpUrl: params.ccpUrl || getCCPUrl(), // REQUIRED
            loginPopup: false, // optional, defaults to `true`
            loginPopupAutoClose: true, // optional, defaults to `false`
            region: params.region || getAwsRegion(), // REQUIRED for `CHAT`, optional otherwise
            softphone: {
              // optional, defaults below apply if not provided
              allowFramedSoftphone: true, // optional, defaults to false
              disableRingtone: false, // optional, defaults to false
              // ringtoneUrl: phoneRingtoneUrl // From public folder
            },
            chat: {
              disableRingtone: false,
              // ringtoneUrl: chatRingtoneUrl // From public folder
            },
            task: {
              disableRingtone: false,
              // ringtoneUrl: digitalRingtoneUrl // From public folder
            },
            ccpAckTimeout: 30000, //optional, defaults to 3000 (ms)
            ccpSynTimeout: 15000, //optional, defaults to 1000 (ms)
            ccpLoadTimeout: 60000, //optional, defaults to 5000 (ms)
          });

          connect.core.onInitialized(() => {
            console.log(lp + "authorization succeeded!");
            this.updateCCPstateToBg(CCP_STATES.CONNECTED);
            this.updateCurrentCCPInstanceToBg(params.instanceAlias);
            // Get initial media devices list
            this.getMediaDevices();
          });
          connect.core.onAuthFail(() => {
            console.log(lp + "Error!!!!!: Session expired! Need to login");
            this.updateCCPstateToBg(CCP_STATES.DISCONNECTED);
          });
          connect.core.onAuthorizeRetriesExhausted(() => {
            console.log(
              lp +
              "Error!!!!!: We have run out of retries to reload the CCP Iframe"
            );
            this.updateCCPstateToBg(CCP_STATES.CLOSED);
          });
          connect.core.onAccessDenied(() => {
            console.log(lp + "Error!!!!!: Access denied");
            this.updateCCPstateToBg(CCP_STATES.CLOSED);
          });
          connect.core.onIframeRetriesExhausted(() => {
            console.log(
              lp +
              "Error!!!!!: We have run out of retries to reload the CCP Iframe"
            );
            console.log(lp + "Logging out due to expired cookies");
            this.updateCCPstateToBg(CCP_STATES.CLOSED);
            Emitter.emit(FAIL_SESSION, "Session expired! Need to login");
          });
          connect.core.onCTIAuthorizeRetriesExhausted(() => {
            console.log(
              lp +
              "Error!!!!!: We have failed CTI API authorization multiple times and we are out of retries"
            );
            this.updateCCPstateToBg(CCP_STATES.CLOSED);
          });
          connect.contact(subscribeToContactEvents);
          connect.agent(subscribeToAgentEvents);
        } else {
          console.log(
            lp +
            "ccp start event called, But current ccp state is already in progress = " +
            CCP_STATES_INDEX[this.currCCPstate]
          );
        }
      } catch (error: any) {
        console.log(
          lp + "Error in starting ccp, Error: " + error.message,
          error
        );
        console.log(
          lp +
          "Error in starting ccp, state is " +
          CCP_STATES_INDEX[this.currCCPstate]
        );
        this.updateCCPstateToBg(CCP_STATES.CLOSED);
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_INFO,
          body: {
            type: INIT_CCP,
            info:
              lp +
              "Error in starting ccp, state is " +
              CCP_STATES_INDEX[this.currCCPstate],
          },
        });
      }
    } else {
      this.updateCCPstateToBg(this.currCCPstate);
    }
  }

  terminatCcpSession() {
    // Remove media devices event listener
    navigator.mediaDevices.removeEventListener('devicechange', this.getMediaDevices);
    connect.core.terminate();
    this.logoutFromConnect();
  }
  terminatCcpInstanceSession() {
    this.terminatCcpSession();
    const containerDiv = document.getElementById(
      CONNECT_STREAMS_DIV_ID
    ) as HTMLElement;
    var iframe = containerDiv.firstElementChild; // assumes there's nothing else in the container
    console.log(lp + "iframe", iframe);
    if (iframe) {
      containerDiv.removeChild(iframe);
    }
    const ccpIframe = document.querySelector(
      'iframe[title="Amazon Connect CCP"]'
    );
    if (ccpIframe) {
      ccpIframe.parentNode?.removeChild(ccpIframe);
      console.log("CCP iframe removed successfully");
    } else {
      console.log("No CCP iframe found");
    }
    this.updateCCPstateToBg(CCP_STATES.CLOSED);
  }

  async setRingerDevice(event: any) {
    if (!connectSession.agent) {
      console.error(lp + "Agent not initialized, cannot set ringer device");
      return;
    }

    if (event.body?.deviceId) {
      try {
        await connectSession.agent.setRingerDevice(event.body.deviceId);
        console.log(lp + "Ringer device set to:", event.body.deviceId);
      } catch (error: any) {
        console.error(lp + "Failed to set ringer device:", error?.message);
      }
    }
  }

  async setSpeakerDevice(event: any) {
    if (!connectSession.agent) {
      console.error(lp + "Agent not initialized, cannot set speaker device");
      return;
    }

    if (event.body?.deviceId) {
      try {
        await connectSession.agent.setSpeakerDevice(event.body.deviceId);
        console.log(lp + "Speaker device set to:", event.body.deviceId);
      } catch (error: any) {
        console.error(lp + "Failed to set speaker device:", error?.message);
      }
    }
  }

  async setMicrophoneDevice(event: any) {
    if (!connectSession.agent) {
      console.error(lp + "Agent not initialized, cannot set microphone device");
      return;
    }

    if (event.body?.deviceId) {
      try {
        await connectSession.agent.setMicrophoneDevice(event.body.deviceId);
        console.log(lp + "Microphone device set to:", event.body.deviceId);
      } catch (error: any) {
        console.error(lp + "Failed to set microphone device:", error?.message);
      }
      Emitter.emit(STATE_UPDATE_MICROPHONE_DEVICE, {
        success: true,
        body: { deviceId: event.body.deviceId }
      });
    }
  }

  logoutFromConnect() {
    fetch(`${(this.ccpUrl || getCCPUrl()).replace("ccp-v2", "logout")}`, {
      credentials: "include",
      mode: "no-cors",
    })
      .then(() => {
        console.log(lp + "Logout successfull from connect");
      })
      .catch((error) =>
        console.log(lp + "Error in loggin out from connect", error)
      );
  }
  updateCCPstateToBg(status: number, currentCcpInstance?: string) {
    this.currCCPstate = status;
    Emitter.emit(CCP_STATE, {
      status: status,
      currentCcpInstance: currentCcpInstance,
    });
  }
  updateCurrentCCPInstanceToBg(instanceAlias: string) {
    Emitter.emit(CCP_INSTANCE, { body: { currentCcpInstance: instanceAlias } });
  }
  getCall() {
    return this.call;
  }
  setCall(call: any) {
    //Below ignore duplicate call ended events
    if (call.self.state === CONTACT_STATE_MAP.ENDED && !this.call?.id) return;

    if (call.self.state === CONTACT_STATE_MAP.ENDED) {
      this.call = null;
    } else {
      this.call = call;
    }
  }
  getVoicemail() {
    return this.voicemail;
  }
  setVoicemail(voicemail: any) {
    //Below ignore duplicate call ended events
    if (voicemail.self.state === CONTACT_STATE_MAP.ENDED && !this.voicemail?.id)
      return;

    if (voicemail.self.state === CONTACT_STATE_MAP.ENDED) {
      this.voicemail = null;
    } else {
      this.voicemail = voicemail;
    }
  }
  getCallback() {
    return this.callback;
  }
  setCallback(callback: any) {
    //Below ignore duplicate call ended events
    if (callback.self.state === CONTACT_STATE_MAP.ENDED && !this.callback?.id)
      return;

    if (callback.self.state === CONTACT_STATE_MAP.ENDED) {
      this.callback = null;
    } else {
      this.callback = callback;
    }
  }
  getConferenceCall() {
    return this.conference;
  }
  setConferenceCall(conference: any) {
    //Below ignore duplicate conference ended events
    if (
      conference.self?.state === CONTACT_STATE_MAP.ENDED &&
      !this.conference?.id
    )
      return;

    if (conference.self?.state === CONTACT_STATE_MAP.ENDED) {
      this.conference = null;
    } else {
      this.conference = conference;
    }
  }
  getMediaDevices(event?: any) {
    const emitEvent = {
      body: {
        success: false,
        mediaDevices: [] as MediaDevice[],
        error: undefined as string | undefined
      }
    };

    const fetchDevices = () => {
      return connect.core
        .getFrameMediaDevices(10000)
        .then((devices: MediaDevice[]) => {
          console.log(
            lp + "Media devices retrieved:",
            devices.length,
            "devices"
          );
          emitEvent.body.success = true;
          emitEvent.body.mediaDevices = devices;
          Emitter.emit(UPDATE_MEDIA_DEVICES_LIST, emitEvent);

          // Re-apply saved device settings after device change
          this.reapplySavedDevices();
        })
        .catch((error: any) => {
          console.error(lp + "Error getting media devices:", error.message);
          emitEvent.body.success = false;
          emitEvent.body.error = error.message;
          Emitter.emit(UPDATE_MEDIA_DEVICES_LIST, emitEvent);
        });
    };

    // Immediate call
    const immediatePromise = fetchDevices();

    // Second call after 2 seconds
    setTimeout(() => {
      fetchDevices();
    }, 2000);

    return immediatePromise;
  }

  reapplySavedDevices() {
    console.log(lp + "Re-applying saved device settings...");

    import("../../store/storeRTK").then((module) => {
      const storeRTK = module.default;
      const state = storeRTK.getState();
      const { speakerDeviceId, microphoneDeviceId, ringtoneOutoutDeviceId } = state.webrtc;

      const settings = [
        { name: "speaker", deviceId: speakerDeviceId, apply: (deviceId: string) => connectSession.agent.setSpeakerDevice(deviceId) },
        { name: "microphone", deviceId: microphoneDeviceId, apply: (deviceId: string) => connectSession.agent.setMicrophoneDevice(deviceId) },
        { name: "ringer", deviceId: ringtoneOutoutDeviceId, apply: (deviceId: string) => connectSession.agent.setRingerDevice(deviceId) },
      ];

      for (const setting of settings) {
        if (!setting.deviceId || !connectSession.agent) continue;
        try {
          Promise.resolve(setting.apply(setting.deviceId)).catch((error) => {
            console.warn(lp + "Error re-applying " + setting.name + " device:", error?.message || error);
          });
          console.log(lp + "Re-applied " + setting.name + " device:", setting.deviceId);
        } catch (error: any) {
          console.warn(lp + "Error re-applying " + setting.name + " device:", error?.message || error);
        }
      }
    }).catch((error) => {
      console.error(lp + "Error loading store for device re-apply:", error);
    });
  }
}
export const connectSession = AmazonConnectSession.getInstance();


/**
 * Register default Events for Call
 */
export const registerSessionHandlers = () => {
  Emitter.on(STOP_CCP, connectSession.terminatCcpInstanceSession);
  Emitter.on(INIT_CCP, connectSession.initiateCcpSession);
  Emitter.on(API_UPDATE_RING_MEDIA_DEVICE, connectSession.setRingerDevice);
  Emitter.on(API_UPDATE_SPEAKER_DEVICE, connectSession.setSpeakerDevice);
  Emitter.on(API_UPDATE_MICROPHONE_DEVICE, connectSession.setMicrophoneDevice);
  console.log(lp + "Session handlers registered");
};
